/**
 * Register limit checkers for Hydranaut features
 * 
 * This file registers all feature limit checkers with the central registry,
 * allowing permission gates and other components to check limits without
 * being coupled to specific store implementations.
 * 
 * Note: Core features (users, etc.) are registered automatically by the App shell.
 * This only registers app-specific features.
 */

import { registerLimitChecker, registerLimitInfoGetter, registerDataChangeSubscriber } from '@nimbitlabs/features';
import { useServicesStore } from '@/stores/servicesStore';
import { useTeamsStore } from '@/stores/teamsStore';
import { useEnvironmentsStore } from '@/stores/environmentsStore';
import { useRunbooksStore } from '@/stores/runbooksStore';
import { useSubscriptionStore } from '@nimbitlabs/features';

/**
 * Initialize app-specific limit checkers
 * Call this once during app initialization
 */
export function registerHydronautLimits(): void {
  // Services
  registerLimitChecker('services', () => 
    useServicesStore.getState().hasReachedLimit()
  );
  registerLimitInfoGetter('services', () => 
    useServicesStore.getState().getServiceLimit()
  );
  registerDataChangeSubscriber('services', (callback) => {
    // Subscribe to services store changes
    const unsubServices = useServicesStore.subscribe(callback);
    // Also subscribe to subscription usage changes (affects limits)
    const unsubUsage = useSubscriptionStore.subscribe(
      (state) => state.usage,
      callback
    );
    // Return combined unsubscribe function
    return () => {
      unsubServices();
      unsubUsage();
    };
  });
  
  // Teams
  registerLimitChecker('teams', () => 
    useTeamsStore.getState().hasReachedLimit()
  );
  registerLimitInfoGetter('teams', () => 
    useTeamsStore.getState().getTeamLimit()
  );
  registerDataChangeSubscriber('teams', (callback) => {
    const unsubTeams = useTeamsStore.subscribe(callback);
    const unsubUsage = useSubscriptionStore.subscribe(
      (state) => state.usage,
      callback
    );
    return () => {
      unsubTeams();
      unsubUsage();
    };
  });
  
  // Environments
  registerLimitChecker('environments', () => 
    useEnvironmentsStore.getState().hasReachedLimit()
  );
  registerLimitInfoGetter('environments', () => 
    useEnvironmentsStore.getState().getEnvironmentLimit()
  );
  registerDataChangeSubscriber('environments', (callback) => {
    const unsubEnvironments = useEnvironmentsStore.subscribe(callback);
    const unsubUsage = useSubscriptionStore.subscribe(
      (state) => state.usage,
      callback
    );
    return () => {
      unsubEnvironments();
      unsubUsage();
    };
  });
  
  // Runbooks
  registerLimitChecker('runbooks', () => 
    useRunbooksStore.getState().hasReachedLimit()
  );
  registerLimitInfoGetter('runbooks', () => 
    useRunbooksStore.getState().getRunbookLimit()
  );
  registerDataChangeSubscriber('runbooks', (callback) => {
    const unsubRunbooks = useRunbooksStore.subscribe(callback);
    const unsubUsage = useSubscriptionStore.subscribe(
      (state) => state.usage,
      callback
    );
    return () => {
      unsubRunbooks();
      unsubUsage();
    };
  });
}
